// Preço de pizza — reproduz EXATAMENTE o servidor (PrecoPizzaService, backend/vitto-domain), que é a fonte
// única de verdade: qualquer divergência aqui é cosmética até o pedido ser enviado, mas o cliente precisa ver
// o MESMO valor que vai ser cobrado (senão o "Adicionar" mostra um preço e o pedido some ou muda no checkout).
//
// O servidor usa BigDecimal com RoundingMode.HALF_EVEN (banker's rounding): a média ponderada dos sabores é
// calculada com scale 8 e só then arredondada para 2 casas junto com a borda. `Math.round` do JS usa "half up"
// e opera em ponto flutuante binário — diverge do servidor em ~3% dos casos (qualquer valor cuja 3ª casa
// decimal pareça terminar em "5" na representação exata do BigDecimal). Para casar 1:1, tudo aqui é feito em
// ARITMÉTICA INTEIRA com BigInt, em unidades de 10^-8 real (a mesma escala do `divide(scale=8, HALF_EVEN)`
// do Java), convertendo de/para centavos só nas bordas.
//
// Espelha PrecoPizzaService.calcularPreco + precoDoSabor + o total final:
//   MAIOR_SABOR            = max(preço do sabor)                              + borda + adicionais
//   MEDIA_PONDERADA        = Σ(preço_i × partes_i) / totalPartes  (scale 8)   + borda + adicionais
//   BASE_MAIS_ACRESCIMOS   = precoBase + [Σ(preço_i × partes_i) / totalPartes (scale 8)] + borda + adicionais
//   total = (sabores [+ borda + adicionais antes do arredondamento final, quando há divisão]).setScale(2, HALF_EVEN)
// Os adicionais DENTRO da pizza (V388) somam como a borda: preço único, em qualquer tamanho.

import type { StorePizza } from './api';

/** Quantos adicionais uma pizza aceita — o PizzaConfiguracao.MAX_ADICIONAIS_POR_PIZZA do servidor. */
export const MAX_ADICIONAIS_POR_PIZZA = 7;

const CENTAVOS_POR_REAL = 100n;
// 1 centavo = 10^-2 real = 10^6 unidades de 10^-8 real — fator de conversão centavos → escala-8.
const ESCALA8_POR_CENTAVO = 1_000_000n;

/** Converte um valor em reais (number) para centavos inteiros (BigInt). Arredonda o double antes de truncar —
 *  preços vêm de JSON como 27.42 etc., já com no máximo 2 casas; o Math.round elimina ruído de ponto flutuante
 *  (27.42 pode chegar como 27.419999999999998).
 *  F2-M5 — `BigInt(Math.round(NaN * 100))` lança RangeError ("The number ... cannot be converted to a BigInt
 *  because it is not an integer"); hoje os call-sites já filtram entradas inválidas antes de chegar aqui, mas
 *  sem uma guarda PRÓPRIA um refator futuro que remova essa proteção travaria o RENDER (chamada síncrona, sem
 *  error boundary). Entrada não-finita (NaN/±Infinity/undefined coagido) vira 0 em vez de lançar. */
function paraCentavos(reais: number): bigint {
  if (!Number.isFinite(reais)) return 0n;
  return BigInt(Math.round(reais * 100));
}

function centavosParaReais(centavos: bigint): number {
  return Number(centavos) / 100;
}

/** Divisão inteira com arredondamento HALF_EVEN (banker's rounding) — mesmo comportamento de
 *  `BigDecimal.divide(divisor, scale, RoundingMode.HALF_EVEN)` do Java quando os operandos já estão na escala
 *  certa para que o resultado seja o valor arredondado. `denominador` é sempre positivo aqui (partes/escala). */
function dividirHalfEven(numerador: bigint, denominador: bigint): bigint {
  if (denominador === 0n) return 0n;
  const negativo = numerador < 0n;
  const n = negativo ? -numerador : numerador;
  const quociente = n / denominador;
  const resto = n % denominador;
  const restoDobrado = resto * 2n;
  let resultado = quociente;
  if (restoDobrado > denominador || (restoDobrado === denominador && quociente % 2n === 1n)) {
    resultado = quociente + 1n;
  }
  return negativo ? -resultado : resultado;
}

/**
 * Tamanho INICIAL da pizza — convenção do configurador (PizzaConfigModal): é sempre o de MENOR `ordem`, com
 * desempate ESTÁVEL pela ordem do array (`Array.prototype.sort` é estável no JS moderno/ES2019+). C3 — este
 * helper é COMPARTILHADO entre StoreView (preço exibido no card do sabor) e QuickView (tamanho pré-selecionado
 * ao abrir a montagem): antes, QuickView usava `pizza.tamanhos[0]` (ordem bruta do array) enquanto o card usava
 * o de menor `ordem` — card e modal podiam abrir/precificar com tamanhos DIFERENTES quando o array não vinha
 * já ordenado por `ordem`. `null` só quando a pizza não tem nenhum tamanho cadastrado (config incompleto).
 */
export function tamanhoInicial(pizza: StorePizza): StorePizza['tamanhos'][number] | null {
  if (pizza.tamanhos.length === 0) return null;
  return [...pizza.tamanhos].sort((a, b) => a.ordem - b.ordem)[0];
}

/**
 * QA-28 C-01 — tamanho inicial PARA UM SABOR específico: o 1º tamanho (menor `ordem`) em que ESSE sabor tem
 * preço cadastrado. O `tamanhoInicial` genérico ignora o sabor e podia abrir a montagem num tamanho em que o
 * sabor pré-marcado (card de sabor da grade) NÃO existe — o checkbox nascia marcado E desabilitado, o
 * "Adicionar" nunca habilitava e o preço do card não batia com o tamanho aberto (beco sem saída em silêncio).
 * Usado pelos DOIS lados (preço do card na grade e tamanho inicial da montagem) para que a vitrine mostre
 * exatamente o preço com que a montagem abre. `null` = sabor sem preço em NENHUM tamanho existente
 * (indisponível de verdade — o chamador nunca deve exibir preço nem deixar adicionar).
 */
export function tamanhoInicialDoSabor(
  pizza: StorePizza,
  saborId: string
): StorePizza['tamanhos'][number] | null {
  const ordenados = [...pizza.tamanhos].sort((a, b) => a.ordem - b.ordem);
  return ordenados.find((t) => precoSaborNoTamanho(pizza, saborId, t.id) != null) ?? null;
}

/**
 * QA-28 A-04 — MENOR preço de um sabor entre os tamanhos que EXISTEM na config (o "a partir de" honesto do
 * card do sabor), junto do nome do tamanho a que ele pertence. Sem isto o card publicava o preço do maior
 * tamanho como se fosse "o" preço da pizza, e o cliente só descobria o Broto mais barato depois de abrir.
 * `null` = sabor sem nenhum preço casado a um tamanho existente.
 */
export function menorPrecoDoSabor(
  pizza: StorePizza,
  saborId: string
): { tamanho: StorePizza['tamanhos'][number]; preco: number } | null {
  let melhor: { tamanho: StorePizza['tamanhos'][number]; preco: number } | null = null;
  for (const t of pizza.tamanhos) {
    const preco = precoSaborNoTamanho(pizza, saborId, t.id);
    if (preco == null) continue;
    if (melhor == null || preco < melhor.preco) melhor = { tamanho: t, preco };
  }
  return melhor;
}

export interface SaborSelecionadoPreco {
  saborId: string;
  partes: number;
}

/**
 * F2-M2 — pizza UTILIZÁVEL: tem ao menos 1 tamanho E ao menos 1 sabor com preço casado a um tamanho que
 * EXISTE de fato. Se o lojista inativar/zerar TUDO (todos os tamanhos, todos os sabores, ou nenhum sabor com
 * preço casado), o backend ainda pode listar o produto com `pizza.tamanhos=[]`/`sabores=[]`/sabores sem
 * `precos` — sem esta guarda o cliente veria "a partir de R$ 0,00" (fallback de array vazio) como se a pizza
 * fosse de graça, e a montagem (QuickView) abriria muda (fieldset sem nenhuma opção, botão travado sem
 * explicação). `false` = o chamador trata como INDISPONÍVEL, nunca como preço 0.
 */
export function pizzaUtilizavel(pizza: StorePizza): boolean {
  if (pizza.tamanhos.length === 0 || pizza.sabores.length === 0) return false;
  const idsTamanho = new Set(pizza.tamanhos.map((t) => t.id));
  return pizza.sabores.some((s) => s.precos.some((pr) => idsTamanho.has(pr.tamanhoId)));
}

/** Preço do sabor NO TAMANHO dado; `undefined` = sabor sem preço cadastrado ali (INDISPONÍVEL — o servidor
 *  recusaria o pedido). Nunca usar `?? 0`: um sabor indisponível não pode ser somado como se custasse zero. */
export function precoSaborNoTamanho(pizza: StorePizza, saborId: string, tamanhoId: string): number | undefined {
  const sabor = pizza.sabores.find((s) => s.id === saborId);
  return sabor?.precos.find((p) => p.tamanhoId === tamanhoId)?.preco;
}

/**
 * Preço total (sabores + borda) em REAIS, com a MESMA regra de arredondamento do servidor.
 * Retorna `null` quando a montagem é inválida para precificar (tamanho inexistente, sem sabores/partes, ou
 * ALGUM sabor selecionado sem preço no tamanho — indisponível) — o chamador decide o que fazer (nunca somar 0).
 */
export function computePizzaPreco(
  pizza: StorePizza,
  tamanhoId: string,
  sabores: SaborSelecionadoPreco[],
  bordaId?: string | null,
  adicionalIds: readonly string[] = [],
): number | null {
  const tamanho = pizza.tamanhos.find((t) => t.id === tamanhoId);
  if (!tamanho) return null;
  const totalPartes = sabores.reduce((n, s) => n + s.partes, 0);
  if (sabores.length === 0 || totalPartes <= 0) return null;

  const precos: { partes: number; centavos: bigint }[] = [];
  for (const sel of sabores) {
    const preco = precoSaborNoTamanho(pizza, sel.saborId, tamanhoId);
    if (preco == null) return null; // sabor indisponível neste tamanho
    precos.push({ partes: sel.partes, centavos: paraCentavos(preco) });
  }

  const bordaCentavos = bordaId ? paraCentavos(pizza.bordas.find((b) => b.id === bordaId)?.preco ?? 0) : 0n;
  // Adicional fora da lista (pausado ou removido depois que a montagem abriu): o servidor recusaria a montagem
  // inteira, então o preço fica indisponível — nunca somado como zero.
  let adicionaisCentavos = 0n;
  for (const id of adicionalIds) {
    const adicional = pizza.adicionais?.find((a) => a.id === id);
    if (!adicional) return null;
    adicionaisCentavos += paraCentavos(adicional.preco);
  }

  if (pizza.politicaPreco === 'MAIOR_SABOR') {
    // Sem divisão: já é um valor exato em centavos (2 casas) — a soma com a borda não precisa de mais
    // arredondamento (setScale(2, HALF_EVEN) num valor já-scale-2 é um no-op), mas mantemos o mesmo caminho
    // final por clareza/consistência com a política ponderada.
    const maiorCentavos = precos.reduce((max, p) => (p.centavos > max ? p.centavos : max), 0n);
    return centavosParaReais(maiorCentavos + bordaCentavos + adicionaisCentavos);
  }

  // MEDIA_PONDERADA / BASE_MAIS_ACRESCIMOS: média em escala 8 (1 unidade = 10^-8 real), HALF_EVEN — espelha
  // BigDecimal.divide(totalPartes, 8, HALF_EVEN) do Java.
  const numeradorCentavos = precos.reduce((s, p) => s + p.centavos * BigInt(p.partes), 0n);
  const media8 = dividirHalfEven(numeradorCentavos * ESCALA8_POR_CENTAVO, BigInt(totalPartes));
  const baseTermo8 =
    pizza.politicaPreco === 'BASE_MAIS_ACRESCIMOS'
      ? paraCentavos(tamanho.precoBase) * ESCALA8_POR_CENTAVO
      : 0n;
  // Borda somada ANTES do arredondamento final (igual ao Java: saboresPreco.add(bordaPreco).setScale(2, HALF_EVEN)
  // — um único arredondamento no total, não dois arredondamentos separados).
  const valor8 = media8 + baseTermo8 + (bordaCentavos + adicionaisCentavos) * ESCALA8_POR_CENTAVO;
  const totalCentavos = dividirHalfEven(valor8, ESCALA8_POR_CENTAVO);
  return centavosParaReais(totalCentavos);
}

/** Reexportado por conveniência de testes/consumidores que só precisam da conversão. */
export const _internal = { paraCentavos, dividirHalfEven, CENTAVOS_POR_REAL };
